import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { awardsService } from '../../services/dataService';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeft, Save, ArrowRight } from 'lucide-react';

const AwardEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    date: '',
    url: '',
    order: 0
  });

  useEffect(() => {
    if (isEditing) {
      fetchAward();
    }
  }, [id]);

  const fetchAward = async () => {
    try {
      const result = await awardsService.getById(id);
      if (result.success) {
        setFormData(result.data);
      } else {
        toast.error('Failed to load award');
      }
    } catch (error) {
      toast.error('Error loading award');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.date || !formData.url) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);

    try {
      let result;
      if (isEditing) {
        result = await awardsService.update(id, formData);
      } else {
        result = await awardsService.create(formData);
      }

      if (result.success) {
        toast.success(`Award ${isEditing ? 'updated' : 'created'} successfully!`);
        setTimeout(() => {
          navigate('/adminaccess/awards');
        }, 500);
      } else {
        toast.error(`Failed to ${isEditing ? 'update' : 'create'} award`);
      }
    } catch (error) {
      toast.error('Error saving award');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white font-mono flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />

      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/awards')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Award'}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-light mb-2">
            {isEditing ? 'Edit Award' : 'Add Award'}
          </h1>
          <p className="text-white/60">
            {isEditing ? '' : 'Add a hackathon win, scholarship, or achievement'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Award Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., 1st Place - HackLouisiana 2024, Dean's Scholarship"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Date <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., March 2024, Fall 2023, 2024"
            />
            <p className="text-white/40 text-xs mt-1">
              Can be specific (March 2024) or general (2024)
            </p>
          </div>

          {/* URL */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Verification URL <span className="text-red-400">*</span>
            </label>
            <input
              type="url"
              name="url"
              value={formData.url}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., https://linkedin.com/posts/..."
            />
            <p className="text-white/40 text-xs mt-1">
              LinkedIn post, certificate link, or any verification URL
            </p>
          </div>

          {/* Order */}
          <div>
            <label className="block text-white/60 text-sm mb-2">Display Order</label>
            <input
              type="number"
              name="order"
              value={formData.order}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="0"
            />
            <p className="text-white/40 text-xs mt-1">
              Lower numbers appear first (0 = first position)
            </p>
          </div>

          {/* Preview */}
          <div className="p-6 rounded-lg border border-white/10 bg-white/5">
            <h3 className="text-sm text-white/40 uppercase tracking-wider mb-4">Preview</h3>
            <a
              href={formData.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-between text-white/90 hover:text-white transition-colors"
            >
              <div>
                <span className="text-white/60 mr-2">•</span>
                <span className="text-base">
                  {formData.title || 'Award Title'}
                </span>
                <span className="text-white/40 ml-2">
                  • {formData.date || 'Date'}
                </span>
              </div>
              <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
            </a>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AwardEditor;